import { themeStyle } from '@src/theming';
import styled, { keyframes, css } from 'styled-components';

import {
  StyledTextRowProps,
  StyledCircleProps,
  StyledRectProps,
  SkeletonLayoutProps,
} from './types';

const pulseAnimation = keyframes`
  50% { opacity: 0.5; }
`;

// Exporting these to enable creation of custom skeleton components
export const baseStyles = css`
  animation: 2s ease-in-out 0.5s infinite ${pulseAnimation};
  background-color: ${themeStyle.varInkLight};
`;

/** The basic building block of a skeleton component */
export const SkeletonPrimitive = styled.div`
  ${baseStyles}
`;

/**
 * Skeleton for a single line of text.
 *
 * You probably shouldn't use this directly in your component. If you want to
 * add a skeleton loader for text content, use the `Text` component and set the
 * `rows` prop.
 *
 * 1. `StyledTextRow` is intended to be nested inside the component that is
 *    loading, hence using `em` to use the parent's font size as height.
 *    The value of `0.6` is used to approximate the character height without
 *    ascenders and descenders.
 * 2. Using `inline-block` so the component inherits the parent's `line-height`
 *    and wraps like text.
 * 3. Since the element won't have any content, giving it a width prevents it
 *    from collapsing. Using `100%` to create a full line of skeleton text.
 * 4. To keep `baseStyles` indeed very basic, the `border-radius` is not
 *    included in them, hence we add it here.
 */
export const StyledTextRow = styled(SkeletonPrimitive).attrs({
  // Using `span` to avoid invalid markup when nesting this inside
  // text elements.
  as: 'span',
})<StyledTextRowProps>`
  height: 0.6em; /* [1] */
  display: inline-block; /* [2] */
  width: 100%; /* [3] */
  border-radius: ${themeStyle.radiusSmall}; /* [4] */
`;

/** Skeleton for a rectangle shape */
export const StyledRect = styled(SkeletonPrimitive)<StyledRectProps>`
  width: ${({ $width }): string | undefined => $width};
  height: ${({ $height }): string | undefined => $height};
  border-radius: ${themeStyle.radiusSmall};
`;

/** Skeleton for a circle shape */
export const StyledCircle = styled(SkeletonPrimitive)<StyledCircleProps>`
  width: ${({ $size }): string | undefined => $size};
  height: ${({ $size }): string | undefined => $size};
  border-radius: 50%;
`;

export const StyledSkeletonLayout = styled.div<
  Pick<SkeletonLayoutProps, '$width' | '$height'>
>`
  width: ${({ $width }): string | undefined => $width};
  height: ${({ $height }): string | undefined => $height};
`;
